import { type FieldMetadata, useInputControl } from "@conform-to/react";
import {
  Listbox as HeadlessListbox,
  ListboxButton as HeadlessListboxButton,
  ListboxOptions as HeadlessListboxOptions,
  type ListboxProps as HeadlessListboxProps,
  ListboxSelectedOption as HeadlessListboxSelectedOption,
} from "@headlessui/react";
import { type VariantProps, cva } from "class-variance-authority";
import { omit } from "radash";
import { classNames } from "../../utils/classNames";
import { ListboxPlaceholder } from "./ListboxPlaceholder";

const listboxButtonVariants = cva(
  [
    // Basic layout
    "group relative block w-full",

    // Background color + shadow applied to inset pseudo element, so shadow blends with border in light mode
    "before:absolute before:inset-px before:rounded-[calc(theme(borderRadius.lg)-1px)] before:bg-form-primary #before:shadow",

    // Background color is moved to control and shadow is removed in dark mode so hide `before` pseudo
    "dark:before:hidden",

    // Hide default focus styles
    "focus:outline-none",

    // Focus ring
    "after:pointer-events-none after:absolute after:inset-0 after:rounded-lg after:ring-inset after:ring-transparent sm:after:data-[focus]:ring-2 sm:after:data-[focus]:ring-focus",

    // Disabled state
    "data-[disabled]:opacity-50 before:data-[disabled]:bg-zinc-950/5 before:data-[disabled]:shadow-none",

    // Read-only state
    "data-[readonly=true]:pointer-events-none data-[readonly=true]:opacity-75 data-[readonly=true]:bg-zinc-950/[0.025] before:data-[readonly=true]:shadow-none",
  ],
  {
    defaultVariants: {
      size: "md",
    },
    variants: {
      size: {
        md: [
          "[&>[data-slot=selected-option]]:text-sm",
          "[&>[data-slot=selected-option]]:min-h-11 [&>[data-slot=selected-option]]:sm:min-h-9",
          "[&>[data-slot=selected-option]]:py-[calc(theme(spacing[3])-1px)] [&>[data-slot=selected-option]]:sm:py-[calc(theme(spacing[2])-1px)]",
        ],
        lg: [
          "[&>[data-slot=selected-option]]:text-base",
          "[&>[data-slot=selected-option]]:min-h-12 [&>[data-slot=selected-option]]:py-[calc(theme(spacing[3])-1px)]",
        ],
      },
    },
  },
);

type ListboxButtonProps = VariantProps<typeof listboxButtonVariants>;

export interface IListboxBaseProps {
  className?: string;
  listClassName?: string;
  placeholder?: React.ReactNode;
  autoFocus?: boolean;
  "aria-label"?: string;
  children?: React.ReactNode;
  readOnly?: boolean;
}

export type ListboxProps = IListboxBaseProps &
  ListboxButtonProps &
  Omit<HeadlessListboxProps, "onChange" | "children" | "value"> & {
    placeholder?: string;
    children: React.ReactNode;
  } & (
    | {
        multiple: true;
        meta?: FieldMetadata<Array<string>>;
        onChange?: (value: Array<string>) => void;
        value?: Array<string>;
      }
    | {
        multiple?: false;
        meta?: FieldMetadata<string | null>;
        onChange?: (value: string | undefined) => void;
        value?: string;
      }
  );

export function Listbox({
  className,
  listClassName,
  autoFocus,
  "aria-label": ariaLabel,
  children: options,
  meta,
  onChange,
  value: propsValue,
  multiple,
  readOnly,
  size,
  ...props
}: ListboxProps) {
  const control = meta
    ? // biome-ignore lint/correctness/useHookAtTopLevel: If 'meta' is not provided then 'useInputControl' should not be called at all
      useInputControl(meta)
    : {
        blur: undefined,
        focus: undefined,
        value: propsValue,
      };

  const allOptions =
    "placeholder" in props && !multiple ? (
      <>
        <ListboxPlaceholder key="placeholder">
          {props.placeholder}
        </ListboxPlaceholder>
        {options}
      </>
    ) : (
      options
    );

  return (
    <HeadlessListbox
      {...("placeholder" in props ? omit(props, ["placeholder"]) : props)}
      onChange={(value) => {
        const prunedValue = Array.isArray(value)
          ? value.filter((value): value is string => !!value)
          : value;

        if ("change" in control) {
          control.change(prunedValue);
        }
        onChange?.(value as string & Array<string>);

        // Make sure the change event has been processed before blurring the control
        setTimeout(() => {
          control.blur?.();
        }, 0);
      }}
      value={control.value ?? (multiple ? [] : "")}
      defaultValue={control.value ?? (multiple ? [] : "")}
      multiple={multiple}
    >
      <HeadlessListboxButton
        autoFocus={autoFocus}
        data-slot="control"
        aria-label={ariaLabel}
        name={meta?.name}
        data-readonly={readOnly}
        className={classNames(listboxButtonVariants({ size }), className)}
      >
        <HeadlessListboxSelectedOption
          as="span"
          options={allOptions}
          data-slot="selected-option"
          className={classNames([
            "[&>[data-slot=placeholder]]:text-tertiary",

            // Basic layout
            "group/selected-option selected-option relative block w-full appearance-none rounded-lg",

            // Horizontal padding
            "pl-[calc(theme(spacing[3.5])-1px)] pr-[calc(theme(spacing.7)-1px)] sm:pl-[calc(theme(spacing.3)-1px)]",

            // Typography
            "text-left text-primary placeholder:text-secondary font-normal forced-colors:text-[CanvasText]",

            // Border
            "border border-zinc-950/10 group-data-[active]:border-zinc-950/20 group-data-[hover]:border-zinc-950/20 dark:border-white/10 dark:group-data-[active]:border-white/20 dark:group-data-[hover]:border-white/20",

            // Background color
            "bg-transparent dark:bg-white/5",

            // Invalid state
            "group-data-[invalid]:border-red-500 group-data-[invalid]:group-data-[hover]:border-red-500 group-data-[invalid]:dark:border-red-600 group-data-[invalid]:data-[hover]:dark:border-red-600",

            // Disabled state
            "group-data-[disabled]:border-zinc-950/20 group-data-[disabled]:opacity-100 group-data-[disabled]:dark:border-white/15 group-data-[disabled]:dark:bg-white/[2.5%] dark:data-[hover]:group-data-[disabled]:border-white/15",
          ])}
          placeholder={
            "placeholder" in props &&
            multiple && (
              <span className="block truncate text-zinc-500">
                {props.placeholder}
              </span>
            )
          }
        />
        <span
          className={classNames(
            "pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2 group-data-[readonly=true]:hidden",
          )}
        >
          <svg
            className="size-5 stroke-zinc-500 group-data-[disabled]:stroke-zinc-600 sm:size-4 dark:stroke-zinc-400 forced-colors:stroke-[CanvasText]"
            viewBox="0 0 16 16"
            aria-hidden="true"
            fill="none"
          >
            <path
              d="M5.75 10.75L8 13L10.25 10.75"
              strokeWidth={1.5}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M10.25 5.25L8 3L5.75 5.25"
              strokeWidth={1.5}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      </HeadlessListboxButton>
      <HeadlessListboxOptions
        as="div"
        anchor="selection start"
        className={classNames(
          // Anchor positioning
          "[--anchor-offset:-1.625rem] [--anchor-padding:theme(spacing.4)] sm:[--anchor-offset:-1.375rem]",

          // Transition styles
          "data-[leave]:ease-in data-[leave]:duration-100 data-[leave]:data-[closed]:opacity-0 data-[leave]:pointer-events-none",

          // Base styles
          "group isolate w-max min-w-[calc(var(--button-width)+1.75rem)] select-none scroll-py-1 rounded-xl p-1",

          // Invisible border that is only visible in `forced-colors` mode for accessibility purposes
          "outline outline-1 outline-transparent focus:outline-none",

          // Handle scrolling when menu won't fit in viewport
          "overflow-y-scroll overscroll-contain",

          // Popover background
          "bg-white/75 backdrop-blur-xl dark:bg-zinc-800/75",

          // Shadows
          "shadow-lg ring-1 ring-accent dark:ring-inset",

          "z-40",

          listClassName,
        )}
      >
        {allOptions}
      </HeadlessListboxOptions>
    </HeadlessListbox>
  );
}
